/** @jsx jsx */

import { Fragment, useState } from 'react';
import { css, jsx } from '@emotion/core';

import {
  Button,
  ButtonGroup,
  Checkbox,
  Document,
  FormLabel,
  Text,
} from '@zapier/common-components';
import { SkeletonBlock } from '@zapier/design-system';
import { Colors } from '@zapier/theme';

import { Interval } from '@zapier/design-system-revenue/src/types';

import { useData as _useData } from './useData';

const containerCss = css`
  margin: 20px auto;
  padding: 40px;
  background-color: ${Colors.white};
  border: 1px solid ${Colors.adamantium};
  max-width: 600px;
`;

const headerCss = css`
  text-align: center;
`;

const listCss = css`
  list-style-type: disc;
  > li {
    margin-bottom: 10px;
  }
`;

const confirmTextCss = css`
  margin-left: 10px;
`;

const buttonGroupCss = css`
  margin-top: 30px;
`;

export type DowngradeWarningPageParams = {
  fromPlanName: string;
  interval: Interval;
  toPlanName: string;
};

type DowngradeWarningPageProps = {
  params: DowngradeWarningPageParams;
  /** For dependency injection testing only */
  useData?: (params: DowngradeWarningPageParams) => ReturnType<typeof _useData>;
};

export const DowngradeWarningPage = ({
  params,
  useData = _useData,
}: DowngradeWarningPageProps) => {
  const {
    continueUrl,
    currentPlanType,
    isLoading,
    teamName,
    toPlanType,
  } = useData(params);

  const [hasConfirmed, setHasConfirmed] = useState(false);

  return (
    <Document title={`Disable Your ${currentPlanType} Plan?`}>
      <div css={containerCss}>
        {isLoading ? (
          <SkeletonBlock height="100%" width="100%" />
        ) : (
          <Fragment>
            <h1 css={headerCss}>
              <Text type="main-head">
                Wait, are you sure you want to pause your {currentPlanType}{' '}
                plan?
              </Text>
            </h1>
            <div>
              <p>
                <Text type="body-large">
                  You’re about to downgrade and pause your{' '}
                  <strong>{teamName}</strong> {currentPlanType} account. Here’s
                  what will change:
                </Text>
              </p>
              <ul css={listCss}>
                {toPlanType !== 'Team' && (
                  <Fragment>
                    <li>
                      <Text type="body-large">
                        Your <strong>{teamName}</strong> {currentPlanType}{' '}
                        account will still be accessible in your accounts.
                      </Text>
                    </li>
                    <li>
                      <Text type="body-large">
                        <strong>Shared folders:</strong> You and your team
                        members will still have access to your shared folders,
                        but none of you will be able to create new ones.
                      </Text>
                    </li>
                    <li>
                      <Text type="body-large">
                        <strong>Shared Zaps:</strong> All shared Zaps will be
                        turned off and won’t be eligible to turn back on unless
                        the account is upgraded to a {currentPlanType} account
                        again.
                      </Text>
                    </li>
                    <li>
                      <Text type="body-large">
                        <strong>Your Zaps:</strong> As the account owner, you’ll
                        still be able to run Zaps in your private folders, but
                        not in any shared folders.
                      </Text>
                    </li>
                    <li>
                      <Text type="body-large">
                        <strong>Your team members:</strong> Members won’t be
                        able to run Zaps in their private folders or shared
                        folders, but they will be able to move the Zaps they own
                        to their personal accounts to run them.
                      </Text>
                    </li>
                    <li>
                      <Text type="body-large">
                        <strong>Connected accounts:</strong> Any private or
                        shared Zaps that use a shared connection will be turned
                        off, and you’ll need to reconnect a personal connection
                        to turn the Zap back on.
                      </Text>
                    </li>
                    <li>
                      <Text type="body-large">
                        <strong>Collaboration:</strong> You won’t be able to
                        invite new team members, but you can always remove team
                        members from your account.
                      </Text>
                    </li>
                  </Fragment>
                )}
                {currentPlanType === 'Company' && (
                  <Fragment>
                    <li>
                      <Text type="body-large">
                        <strong>SAML Single Sign-On:</strong> New and existing
                        members will no longer be able to use your company’s
                        single sign-on provider for authenticating into Zapier.
                      </Text>
                    </li>
                    <li>
                      <Text type="body-large">
                        <strong>User provisioning:</strong> New and existing
                        members will no longer be provisioned by your identity
                        management system.
                      </Text>
                    </li>
                    <li>
                      <Text type="body-large">
                        <strong>App access control:</strong> App specific
                        permissions will no longer be enforced within your
                        account.
                      </Text>
                    </li>
                    <li>
                      <Text type="body-large">
                        <strong>Custom data rentention:</strong> Your data will
                        now be retained for 90 days.
                      </Text>
                    </li>
                  </Fragment>
                )}
              </ul>
            </div>
            <p>
              <Checkbox
                checked={hasConfirmed}
                id="confirm-team-downgrade"
                name="confirm-team-downgrade"
                onChange={(e: any) => setHasConfirmed(e.checked)}
                value="confirm-team-downgrade"
              />
              <FormLabel htmlFor="confirm-team-downgrade" isInline={true}>
                <Text type="body">
                  <span css={confirmTextCss}>
                    I understand that I, and my {currentPlanType} members, will
                    lose access to the features listed above and confirm my
                    downgrade.
                  </span>
                </Text>
              </FormLabel>
            </p>
            <div css={buttonGroupCss}>
              <ButtonGroup
                direction="row"
                justify="space-between"
                propsQueries={{
                  phoneOnly: { direction: 'column', isReversed: true },
                }}
              >
                <Button color="secondary" href={'/app/billing/plans'}>
                  Keep My Plan
                </Button>
                <Button
                  color="primary"
                  href={continueUrl}
                  isDisabled={!hasConfirmed}
                >
                  Confirm My Downgrade
                </Button>
              </ButtonGroup>
            </div>
          </Fragment>
        )}
      </div>
    </Document>
  );
};
